import { hasValidCommercialProposal } from './contractIntegrity';

/**
 * Etapa que debe quedar en Firestore.
 * El servidor usa la misma lógica en pipelineStageAlign.cjs.
 * @returns {'SIGNING'|'IMPLEMENTATION'|null}
 */
export function pipelineStageToPersist(contract = {}) {
  const stored = String(contract.pipelineStage || '').toUpperCase();
  const legal = String(contract.legalStatus || '').toUpperCase();
  const sales = String(contract.salesStatus || '').toUpperCase();
  if (legal !== 'APPROVED' || sales !== 'APPROVED') return null;

  const behind = new Set(['SALES_REVIEW', 'LEGAL_REVIEW', 'SIGNING']);
  if (!behind.has(stored)) return null;

  if (contract.fad_zip_url) return 'IMPLEMENTATION';
  if (stored !== 'SIGNING') return 'SIGNING';
  return null;
}

export function buildPipelineStageAlignPatch(contract = {}, now = new Date().toISOString()) {
  const next = pipelineStageToPersist(contract);
  if (!next) return null;
  const hist = Array.isArray(contract.pipelineHistory) ? contract.pipelineHistory.slice() : [];
  const hasStage = hist.some((h) => String(h?.stage || '').toUpperCase() === next);
  if (!hasStage) {
    hist.push({ stage: next, date: now, source: 'stage_align' });
  }
  return {
    pipelineStage: next,
    pipelineHistory: hist,
    lastUpdate: now
  };
}

export {
  isCommercialProposalComplete,
  canAdvanceOnboardingToLegal,
  canSkipLegalToImplementation
} from './contractIntegrity';

const COMMERCIAL_PIPELINE_STAGES = new Set([
  'SALES_REVIEW', 'LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION', 'BILLING', 'ONBOARDING'
]);

const ADVANCED_PIPELINE_STAGES = new Set([
  'LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION', 'BILLING', 'CLOSED', 'VERIFIED'
]);

/** Cliente ya facturado u operativo. Una invitación de onboarding abre ciclo en Rev. Comercial. */
const TERMINAL_OPERATIONAL_STAGES = new Set(['VERIFIED', 'CLOSED', 'BILLING']);

/** Embudo en curso: reenviar la invitación no regresa la tarjeta. */
const IN_FLIGHT_PIPELINE_STAGES = new Set(['LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION']);

/**
 * Un reenvío de onboarding no debe regresar a Rev. Com. un expediente
 * que ya pasó Legal, ya se firmó o ya está en implementación.
 */
export function shouldPreserveAdvancedPipelineStage(contract = {}) {
  const stage = String(contract.pipelineStage || '').toUpperCase();
  if (ADVANCED_PIPELINE_STAGES.has(stage)) return true;
  const legal = String(contract.legalStatus || '').toUpperCase();
  if (legal === 'APPROVED' || legal === 'EASYLEX_SENT') return true;
  if (contract.fad_zip_url) return true;
  const hist = Array.isArray(contract.pipelineHistory) ? contract.pipelineHistory : [];
  return hist.some((h) => ADVANCED_PIPELINE_STAGES.has(String(h?.stage || '').toUpperCase()));
}

/**
 * Invitación de onboarding sobre un cliente ya en Cerrado, Factura o VERIFIED.
 * La tarjeta debe quedar en Rev. Comercial, no en Cierre.
 * Legal, Firma e Implementación no se regresan.
 */
export function shouldOpenSalesReviewOnOnboardingInvite(contract = {}) {
  const stage = String(contract?.pipelineStage || '').toUpperCase();
  if (IN_FLIGHT_PIPELINE_STAGES.has(stage)) return false;
  return TERMINAL_OPERATIONAL_STAGES.has(stage);
}

/** Patch para sacar de Cierre un operativo al enviar la invitación de alta. */
export function onboardingInvitePipelinePatch(contract = {}, now = new Date().toISOString()) {
  if (!shouldOpenSalesReviewOnOnboardingInvite(contract)) return null;
  const hist = Array.isArray(contract.pipelineHistory) ? contract.pipelineHistory.slice() : [];
  hist.push({
    stage: 'SALES_REVIEW',
    date: now,
    source: 'onboarding_invite',
    note: 'Invitación de onboarding: de Cierre a Rev. Comercial'
  });
  return {
    pipelineStage: 'SALES_REVIEW',
    salesStatus: 'PENDING',
    estado: 'Negociación',
    allowPipelineRegress: true,
    salesPipelineVisible: true,
    pipelineHistory: hist,
    ...(contract.isRenewal ? { stage: 'PROPUESTA' } : {})
  };
}

const COLUMN_STAGE_ALIAS = {
  ONBOARDING: 'SALES_REVIEW',
  VERIFIED: 'CLOSED',
  IMPLEMENTACION: 'IMPLEMENTATION'
};

/**
 * Etapa de la columna. Es `pipelineStage`.
 * No se inventa otra columna por ZIP firmado, legal o salesStatus.
 * Solo traduce alias que no tienen columna propia.
 */
export function pipelineColumnStage(contract = {}) {
  const stage = String(contract?.pipelineStage || '').toUpperCase();
  if (!stage) return null;
  return COLUMN_STAGE_ALIAS[stage] || stage;
}

/** Legal aprobó y hay ZIP FAD: el contrato ya está firmado, aunque el kanban se haya quedado atrás. */
export function isSignedReadyForImplementation(contract = {}) {
  if (String(contract.salesStatus || '').toUpperCase() === 'PENDING') return false;
  const legal = String(contract.legalStatus || '').toUpperCase();
  if (legal !== 'APPROVED' && legal !== 'EASYLEX_SENT') return false;
  return Boolean(contract.fad_zip_url);
}

/** Lead reactivado tras una baja: volvió a estar activo y avanzó a pipeline/conversión. */
export function isReactivatedCommercialLead(record = {}) {
  if (!record || record.masterStatus === 'BAJA') return false;
  const phase = Number(record.phase);
  if (phase === 4 || phase === 7) return false;
  if (record.phase3Result === 'CIERRE_BAJA') return false;
  return record.masterStatus === 'CONVERTED' || phase >= 3;
}

/** DESCARTADO de fase 2 que sobrevivió a la reactivación del lead (falsa baja). */
export function hasStaleDiscardFlag(record = {}) {
  return record?.phase2Result === 'DESCARTADO' && isReactivatedCommercialLead(record);
}

/** Lead/prospecto en baja comercial (fase 4/7 o masterStatus BAJA). */
export function isCrmLeadCommercialBaja(record = {}) {
  if (!record) return false;
  const phase = Number(record.phase);
  if (record.masterStatus === 'BAJA' || phase === 4 || phase === 7) return true;
  if (record.phase3Result === 'CIERRE_BAJA') return true;
  // Un DESCARTADO de fase 2 no es baja si el lead ya se reactivó y avanzó.
  return record.phase2Result === 'DESCARTADO' && !isReactivatedCommercialLead(record);
}

/** Estado comercial de un lead con overrides de una actualización en curso. */
export function mergeLeadStatusFields(lead = {}, overrides = {}) {
  return {
    masterStatus: overrides.masterStatus ?? lead.masterStatus,
    phase: overrides.phase ?? lead.phase,
    phase2Result: overrides.phase2Result ?? lead.phase2Result,
    phase3Result: overrides.phase3Result ?? lead.phase3Result
  };
}

/** CSF, ID o acta guardados en Drive (expediente legal). */
export function hasLegalOnboardingDocuments(c) {
  return Boolean(c?.csf_url || c?.id_url || c?.acta_url);
}

/** Falta el expediente que el cliente carga en el alta (común en altas desde Por Cobrar). */
export function needsClientExpedienteDocuments(c) {
  if (!c) return false;
  return !hasLegalOnboardingDocuments(c);
}

/**
 * ¿Mostrar “invitar / reenviar onboarding” en Rev. Com. / modal comercial?
 * Incluye operativos Por Cobrar sin CSF/ID/acta (aunque sigan en VERIFIED tras reabrir).
 */
export function canInviteClientOnboarding(c) {
  if (!c?.id) return false;
  if (hasLegalOnboardingDocuments(c)) return false;

  if (c.onboardingCrmStatus === 'PENDIENTE_CLIENTE') return true;
  if (c.onboardingSentAt || c.onboardingStatus === 'ENVIADA') return true;

  const stage = String(c.pipelineStage || '').toUpperCase();
  if (stage === 'SALES_REVIEW' || stage === 'ONBOARDING' || !stage) return true;
  // Reabiertos / visibles aún en VERIFIED pero sin expediente legal
  if (stage === 'VERIFIED' && c.salesPipelineVisible !== false) return true;

  return false;
}

/** URL del Google Sheet con la lista de usuarios (si existe). */
export function getUserListSheetUrl(c) {
  return c?.lista_usuarios_url || c?.userListSheetUrl || null;
}

/** Roles que abren y suben la hoja de lista de usuarios. El ejecutivo comercial no. */
const USER_LIST_SHEET_VIEW_ROLES = new Set([
  'admin',
  'dirección',
  'direccion',
  'director',
  'ceo',
  'skillsync & nexus care',
]);

/** Dirección, admin y SkillSync, Nexus Care & Support abren y suben la lista de usuarios. */
export function canViewUserListSheet(userRole) {
  return USER_LIST_SHEET_VIEW_ROLES.has((userRole || '').toLowerCase().trim());
}

/** Lista de usuarios subida por el cliente (paso ?step=userlist o carga manual). */
export function hasUserListUploaded(c) {
  if (c?.userListStatus === 'COMPLETED') return true;
  return Boolean(getUserListSheetUrl(c));
}

/** Misma liga del correo de bienvenida: el cliente sube la lista y, si aplica, marca licencias. */
export function getClientUserListUrl(contract) {
  const id = contract?.id;
  if (!id || typeof window === 'undefined') return '';
  return `${window.location.origin}/?token=${encodeURIComponent(id)}&step=userlist`;
}

/** Cualquier artefacto de onboarding (legal o lista de usuarios). */
export function hasOnboardingDocuments(c) {
  return hasLegalOnboardingDocuments(c) || hasUserListUploaded(c);
}

/** Cliente terminó el formulario de alta (servidor o expediente legal en Drive). */
export function isOnboardingExpedienteComplete(c) {
  return c?.onboardingCrmStatus === 'COMPLETADO' || hasLegalOnboardingDocuments(c);
}

export function hasCrmOnboardingInviteSent(c) {
  if (c?.onboardingSentAt || c?.onboardingStatus === 'ENVIADA' || c?.onboardingCrmStatus) return true;
  const hist = c?.history || [];
  return hist.some(
    (h) => h.action === 'INVITACION_ONBOARDING' || (h.title || '').includes('Onboarding Enviado')
  );
}

/**
 * Marcadores de flujo comercial real (onboarding, propuesta, historial de embudo).
 * Un crmLeadId por sí solo no cuenta.
 */
export function hasRealCommercialPipelineMarkers(c) {
  if (!c) return false;
  if (c.salesPipelineVisible === true) return true;
  if (hasOnboardingDocuments(c)) return true;
  if (hasCrmOnboardingInviteSent(c)) return true;
  if (c.propuesta_url || c.shortId) return true;
  if (hasValidCommercialProposal(c?.proposal) && c?.proposal?.source !== 'operational') return true;
  return (c?.pipelineHistory || []).some((e) => COMMERCIAL_PIPELINE_STAGES.has(e?.stage));
}

export function isBajaCommercialContract(c) {
  if (!c) return false;
  if (c.salesPipelineVisible === true) return false;
  if (c.masterStatus === 'BAJA') return true;
  if (c.crmLeadMasterStatus === 'BAJA') return true;
  const phase = Number(c.phase);
  const crmPhase = Number(c.crmLeadPhase);
  if (phase === 4 || phase === 7 || crmPhase === 4 || crmPhase === 7) return true;
  if (c.removedFromSalesPipelineReason === 'CRM_BAJA_COMERCIAL') return true;
  const statusHist = (c.history || [])
    .filter((h) => h?.masterStatus != null || h?.phase != null)
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  const latest = statusHist[0];
  if (!latest) return false;
  return latest.masterStatus === 'BAJA' || Number(latest.phase) === 4 || Number(latest.phase) === 7;
}

/** Deal que pasó por embudo comercial, no solo vínculo CRM automático. */
export function hasCommercialPipelineEntry(c) {
  return Boolean(
    c?.salesPipelineVisible === true
    || hasRealCommercialPipelineMarkers(c)
  );
}

/** Flujo comercial explícito (no solo sync operativo / backfill). */
export function hasExplicitCommercialFlow(c) {
  return hasRealCommercialPipelineMarkers(c);
}

/**
 * ¿Mostrar expediente en tablero comercial (Dashboard Principal)?
 */
export function shouldShowInSalesPipeline(c) {
  if (!c) return false;
  if (isBajaCommercialContract(c)) return false;
  if (c.isRenewal) return true;

  if (c.pipelineStage === 'VERIFIED') {
    return hasRealCommercialPipelineMarkers(c);
  }

  if (c.salesPipelineVisible === true) return true;
  if (hasOnboardingDocuments(c)) return true;
  if (c.crmLeadId && hasCrmOnboardingInviteSent(c)) return true;
  if (!c.crmLeadId) return true;
  if (c.salesStatus === 'APPROVED' || c.legalStatus === 'APPROVED') return true;
  if (c.pipelineStage && c.pipelineStage !== 'SALES_REVIEW' && c.pipelineStage !== 'ONBOARDING') {
    return true;
  }
  return false;
}

/** Expediente existente pero oculto del kanban comercial (cerrado operativo / baja). */
export function needsSalesPipelineReopen(c) {
  if (!c?.id || c.isRenewal) return false;
  return !shouldShowInSalesPipeline(c);
}

/** Cerrado/operativo que admin puede devolver a Rev. Comercial (upsell). */
export function canSendToCommercialReview(c) {
  if (!c?.id || c.isRenewal) return false;
  const stage = String(c.pipelineStage || '').toUpperCase();
  return stage === 'VERIFIED' || stage === 'CLOSED' || stage === 'BILLING';
}

/**
 * Un solo CTA: reabrir → Rev. Comercial (visibilidad + etapa).
 * Cubre ocultos del kanban y operativos VERIFIED/CLOSED/BILLING.
 */
export function canReopenToCommercialReview(c) {
  if (!c?.id || c.isRenewal) return false;
  return canSendToCommercialReview(c) || needsSalesPipelineReopen(c);
}

/**
 * El tablero CRM no lista convertidos, onboarded ni eliminados.
 * Una reapertura autorizada deja masterStatus PROSPECTO_ACTIVO: ese lead se muestra
 * aunque isOnboarded siga en true del ciclo anterior.
 */
export function isHiddenFromCrmBoard(lead) {
  if (!lead) return true;
  const status = String(lead.masterStatus || '');
  if (status === 'ELIMINADO') return true;
  if (status === 'PROSPECTO_ACTIVO') return false;
  if (status === 'CONVERTED' || lead.isOnboarded) return true;
  return false;
}

/** ¿Vincular lead CRM a contrato existente (backfill / asignación)? */
export function shouldLinkCrmLeadToContract(contract = {}, lead = {}) {
  if (!lead?.id || lead.isContract) return false;
  if (isCrmLeadCommercialBaja(lead)) return false;
  if (contract.pipelineStage === 'VERIFIED' && !hasRealCommercialPipelineMarkers(contract)) return false;
  return true;
}

/** Patch Firestore para ocultar expediente del tablero comercial. */
export function buildHideFromSalesPipelinePatch(reason, lead = {}) {
  const patch = {
    salesPipelineVisible: false,
    removedFromSalesPipelineReason: reason
  };
  if (lead.id) {
    patch.crmLeadId = lead.id;
    patch.crmLeadMasterStatus = lead.masterStatus || 'BAJA';
    patch.crmLeadPhase = Number(lead.phase) || 4;
  }
  if (isCrmLeadCommercialBaja(lead)) {
    patch.masterStatus = 'BAJA';
    patch.phase = 4;
  }
  return patch;
}

export { COMMERCIAL_PIPELINE_STAGES };
